import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, setDoc, getDocs, deleteDoc, updateDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react';
import { 
  Bars3Icon, 
  XMarkIcon, 
  ChevronRightIcon, 
  ChevronLeftIcon, 
  PlusIcon,
  TrashIcon,
  PencilIcon,
  PhoneIcon,
  DocumentIcon,
  SparklesIcon,
  Cog6ToothIcon,
  MicrophoneIcon,
  ChatBubbleLeftRightIcon,
  FolderIcon,
  SpeakerWaveIcon,
  PlayIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { GiDominoMask, GiRadarSweep, GiSoundWaves, GiEchoRipples, GiSpeedometer } from "react-icons/gi";
import { MdContacts } from "react-icons/md";
import { useLocation, Link, useNavigate } from 'react-router-dom';

// Function to get navigation items
function getFilteredNavigation(pathname) {
  return [
    { name: 'Home', href: '/overview', icon: GiSpeedometer, current: pathname === '/overview' },
    { name: 'Relay', href: '/dashboard', icon: GiDominoMask, current: pathname === '/dashboard' },
    { name: 'Messages', href: '/dashboard/trace', icon: GiEchoRipples, current: pathname === '/dashboard/trace' },
    { name: 'World Call', href: '/world-call', icon: GiSoundWaves, current: pathname === '/world-call' },
    { name: 'Contacts', href: '/contacts', icon: MdContacts, current: pathname === '/contacts' },
    { name: 'Monitor', href: '/dashboard/monitor', icon: GiRadarSweep, current: pathname === '/dashboard/monitor' },
  ];
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function AIAssistantManager() {
  const [assistants, setAssistants] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [user, setUser] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showBucketDialog, setShowBucketDialog] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  
  // Form states
  const [assistantName, setAssistantName] = useState('');
  const [assistantInstructions, setAssistantInstructions] = useState('You are a helpful AI assistant that can answer phone calls and help with various tasks. Be friendly, professional, and concise in your responses.');
  const [assistantGreeting, setAssistantGreeting] = useState('Hello! This is your AI assistant. How can I help you today?');
  const [selectedVoice, setSelectedVoice] = useState('Telnyx.NaturalHD.Astra');
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState([]);
  const [selectedKnowledgeFolders, setSelectedKnowledgeFolders] = useState([]);
  const [bucketName, setBucketName] = useState('');
  const [bucketDescription, setBucketDescription] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [selectedBucket, setSelectedBucket] = useState('');
  const [availableBuckets, setAvailableBuckets] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState('url'); // 'url' or 'files'
  const [showKnowledgeDialog, setShowKnowledgeDialog] = useState(false);
  const [selectedAssistantForKnowledge, setSelectedAssistantForKnowledge] = useState(null);
  const [assistantKnowledgeFolders, setAssistantKnowledgeFolders] = useState([]);
  const [userBuckets, setUserBuckets] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  
  const location = useLocation();
  const navigate = useNavigate();
  const navigation = getFilteredNavigation(location.pathname);
  const settingsNavItem = { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, current: location.pathname === '/settings' };

  // Available voice options
  const voiceOptions = [
    { 
      value: 'Telnyx.NaturalHD.Astra', 
      label: 'Astra - Professional Female', 
      description: 'Clear, professional voice perfect for business',
      gender: 'female',
      tone: 'professional'
    },
    { 
      value: 'Telnyx.KokoroTTS.af_heart', 
      label: 'Heart - Friendly Female', 
      description: 'Warm, friendly voice great for customer service',
      gender: 'female',
      tone: 'friendly'
    },
    { 
      value: 'Telnyx.KokoroTTS.af_bella', 
      label: 'Bella - Professional Female', 
      description: 'Confident, professional female voice',
      gender: 'female',
      tone: 'professional'
    },
    { 
      value: 'Telnyx.KokoroTTS.am_michael', 
      label: 'Michael - Professional Male', 
      description: 'Professional male voice for business calls',
      gender: 'male',
      tone: 'professional'
    },
    { 
      value: 'Telnyx.KokoroTTS.am_adam', 
      label: 'Adam - Casual Male', 
      description: 'Casual, approachable male voice',
      gender: 'male',
      tone: 'casual'
    },
  ];

  // Instruction templates for different business types
  const instructionTemplates = [
    {
      name: 'Flower Shop',
      description: 'Perfect for florists and flower delivery services',
      instructions: `You are a helpful assistant for [Your Flower Shop Name]. You help customers with flower orders, deliveries, and questions about our arrangements.

Our Hours: Monday-Saturday 9am-6pm, Sunday 10am-4pm
Location: [Your Address]
Delivery: Available within 10 miles, $5 delivery fee

Services:
- Fresh flower arrangements for all occasions
- Wedding bouquets and centerpieces  
- Funeral arrangements
- Plant care advice
- Same-day delivery (if ordered before 2pm)

Be friendly, helpful, and always ask how you can make their special occasion perfect. If you can't answer something, politely let them know they can speak with our florist during business hours.`,
      greeting: 'Hello! Thank you for calling [Your Flower Shop Name]. I\'m here to help you with flower arrangements, deliveries, or any questions about our beautiful blooms. How can I make your day more colorful?'
    },
    {
      name: 'Restaurant',
      description: 'Great for restaurants, cafes, and food service',
      instructions: `You are a helpful assistant for [Your Restaurant Name]. You help customers with reservations, menu questions, hours, and takeout orders.

Our Hours: [Update with your hours]
Location: [Your Address]
Phone for Reservations: [Your Number]

What we offer:
- Dine-in with full menu
- Takeout and delivery
- Private party bookings
- Daily specials
- Dietary accommodations (vegetarian, gluten-free options available)

Be warm and welcoming. Help customers make reservations, answer menu questions, and provide information about our restaurant. For complex orders or specific dietary needs, connect them with our kitchen staff.`,
      greeting: 'Hello! Welcome to [Your Restaurant Name]. I\'m here to help with reservations, menu questions, or information about our delicious food. How can I assist you today?'
    },
    {
      name: 'Hair Salon',
      description: 'Ideal for hair salons, spas, and beauty services',
      instructions: `You are a helpful assistant for [Your Salon Name]. You help clients with appointments, services, and questions about our treatments.

Our Hours: [Update with your hours]
Location: [Your Address]

Services we offer:
- Haircuts and styling (men, women, children)
- Hair coloring and highlights
- Perms and chemical treatments
- Special occasion styling
- Hair care consultations

Be professional yet friendly. Help clients understand our services, pricing, and availability. For booking appointments, guide them on what information we need (preferred stylist, service type, timing). Always mention our consultation options for major changes.`,
      greeting: 'Hello! Thank you for calling [Your Salon Name]. I\'m here to help you with appointments, service information, or questions about our hair care treatments. How can I help you look and feel your best?'
    },
    {
      name: 'Medical Office',
      description: 'Professional template for medical practices',
      instructions: `You are a helpful assistant for [Your Practice Name]. You help patients with appointment scheduling, general information, and directing urgent matters appropriately.

IMPORTANT: You cannot provide medical advice. For medical questions, emergencies, or urgent concerns, direct patients to speak with medical staff or call emergency services.

Office Hours: [Update with your hours]
Location: [Your Address]
Emergency: For medical emergencies, call 911

Services:
- Routine checkups and preventive care
- Appointment scheduling
- General health information
- Insurance and billing questions (direct to billing department)

Be professional, empathetic, and helpful. Protect patient privacy - never discuss specific medical information. For anything medical-related, politely connect them with appropriate medical staff.`,
      greeting: 'Hello, thank you for calling [Your Practice Name]. I\'m here to help with appointments and general information. For medical questions or urgent matters, I\'ll connect you with our medical staff. How may I assist you today?'
    },
    {
      name: 'General Business',
      description: 'Flexible template for any type of business',
      instructions: `You are a helpful assistant for [Your Business Name]. You help customers with questions about our services, hours, location, and general information.

Our Hours: [Update with your hours]
Location: [Your Address]
Services: [Update with your main services]

Be professional, friendly, and helpful. Answer questions about our business, provide information about services, and help customers get connected with the right person for their needs. If you don't know something, politely let them know and offer to have someone call them back.`,
      greeting: 'Hello! Thank you for calling [Your Business Name]. I\'m here to help answer questions about our services and assist you however I can. How may I help you today?'
    }
  ];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await Promise.all([
          loadAssistants(currentUser.uid),
          loadPhoneNumbers(currentUser.uid),
          loadBuckets(currentUser.uid)
        ]);
      } else {
        setUser(null);
        setAssistants([]);
        setPhoneNumbers([]);
        setBuckets([]);
      }
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, []);

  // Load user buckets when bucket dialog opens
  useEffect(() => {
    if (showBucketDialog && user) {
      loadUserBuckets().then(buckets => {
        setUserBuckets(buckets);
        if (buckets.length > 0) {
          setSelectedBucket(buckets[0].telnyxBucketName); // Auto-select first bucket
        }
      });
    }
  }, [showBucketDialog, user]);

  // Load user buckets when create assistant dialog opens
  useEffect(() => {
    if (showCreateDialog && user) {
      loadUserBuckets().then(buckets => {
        setUserBuckets(buckets);
      });
    }
  }, [showCreateDialog, user]);

  const loadAssistants = async (uid) => {
    try {
      const assistantsRef = collection(db, 'aiAssistant', uid, 'assistants');
      const snapshot = await getDocs(assistantsRef);
      const assistantData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAssistants(assistantData);
    } catch (error) {
      console.error('Error loading assistants:', error);
    }
  };

  const loadPhoneNumbers = async (uid) => {
    try {
      const phoneAliasesRef = collection(db, 'userAliases', uid, 'phoneAliases');
      const snapshot = await getDocs(phoneAliasesRef);
      const phoneData = snapshot.docs.map(doc => ({
        id: doc.id,
        number: doc.id,
        ...doc.data()
      })).filter(phone => 
        phone.type === 'voip' && 
        phone.connectionStatus && 
        phone.connectionStatus.toLowerCase() === 'active'
      );
      setPhoneNumbers(phoneData);
    } catch (error) {
      console.error('Error loading phone numbers:', error);
    }
  };

  const loadBuckets = async (uid) => {
    try {
      const bucketsRef = collection(db, 'assistantsBucket', uid, 'buckets');
      const snapshot = await getDocs(bucketsRef);
      const bucketData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBuckets(bucketData);
    } catch (error) {
      console.error('Error loading buckets:', error);
    }
  };

  const createAssistant = async () => {
    if (!assistantName.trim()) {
      setCreateError('Assistant name is required');
      return;
    }
    
    if (selectedPhoneNumbers.length === 0) {
      setCreateError('Please select at least one phone number');
      return;
    }

    setIsCreating(true);
    setCreateError('');

    try {
      // Get user's auth token
      const idToken = await user.getIdToken();

      // Call API to create Telnyx assistant
      const response = await fetch('/api/ai-assistant/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
                           uid: user.uid,
                 name: assistantName,
                 instructions: assistantInstructions,
                 greeting: assistantGreeting,
                 voice: selectedVoice,
                 phoneNumbers: selectedPhoneNumbers,
                 knowledgeFolders: selectedKnowledgeFolders
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to create assistant');
      }

      // Save to Firestore
      const assistantRef = doc(db, 'aiAssistant', user.uid, 'assistants', result.assistantId);
      await setDoc(assistantRef, {
        name: assistantName,
        instructions: assistantInstructions,
        greeting: assistantGreeting,
        voice: selectedVoice,
        phoneNumbers: selectedPhoneNumbers,
        bucketID: null, // Will be set when bucket is created
        created: serverTimestamp(),
        status: 'active',
        telnyxAssistantId: result.assistantId
      });

      // Refresh assistants
      await loadAssistants(user.uid);
      
      // Reset form
      setAssistantName('');
      setAssistantInstructions('You are a helpful AI assistant that can answer phone calls and help with various tasks. Be friendly, professional, and concise in your responses.');
      setAssistantGreeting('Hello! This is your AI assistant. How can I help you today?');
      setSelectedVoice('Telnyx.NaturalHD.Astra');
      setSelectedPhoneNumbers([]);
      setSelectedKnowledgeFolders([]);
      setShowCreateDialog(false);
      
    } catch (error) {
      console.error('Error creating assistant:', error);
      setCreateError(error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const createBucket = async () => {
    if (!bucketName.trim()) {
      alert('Bucket name is required');
      return;
    }

    try {
      // Get user's auth token
      const idToken = await user.getIdToken();

      // Call API to create Telnyx bucket
      const response = await fetch('/api/ai-assistant/create-bucket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          uid: user.uid,
          name: bucketName,
          description: bucketDescription
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to create bucket');
      }

      // Save to Firestore
      const bucketRef = doc(db, 'assistantsBucket', user.uid, 'buckets', result.bucketId);
      await setDoc(bucketRef, {
        name: bucketName,
        description: bucketDescription,
        created: serverTimestamp(),
        telnyxBucketId: result.bucketId,
        documents: [],
        embeddingStatus: 'ready'
      });

      // Refresh buckets
      await loadBuckets(user.uid);
      
      // Reset form
      setBucketName('');
      setBucketDescription('');
      setShowBucketDialog(false);
      
    } catch (error) {
      console.error('Error creating bucket:', error);
      
      // Show user-friendly error message
      if (error.message.includes('temporarily unavailable')) {
        alert('Knowledge Folders are coming soon! This feature will allow you to upload documents and website links to train your AI assistant.');
      } else {
        alert('Error creating knowledge folder: ' + error.message);
      }
    }
  };

  const loadUserBuckets = async () => {
    try {
      const token = await user.getIdToken();
      
      const response = await fetch('/api/ai-assistant/list-user-buckets', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (response.ok) {
        return result.userBuckets || [];
      } else {
        console.error('Error loading user buckets:', result.message);
        return [];
      }
    } catch (error) {
      console.error('Error loading user buckets:', error);
      return [];
    }
  };

  const assignBucket = async (userGivenName) => {
    try {
      const token = await user.getIdToken();
      
      const response = await fetch('/api/ai-assistant/assign-bucket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          userGivenName: userGivenName,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to assign knowledge folder');
      }

      return result;
    } catch (error) {
      console.error('Error assigning bucket:', error);
      throw error;
    }
  };

  const embedURL = async (url, bucketName) => {
    try {
      const token = await user.getIdToken();
      
      const response = await fetch('/api/ai-assistant/embed-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          url: url,
          bucketName: bucketName,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to embed URL');
      }

      return result;
    } catch (error) {
      console.error('Error embedding URL:', error);
      throw error;
    }
  };

  const uploadFiles = async (files, bucketName) => {
    try {
      const token = await user.getIdToken();
      const formData = new FormData();
      
      formData.append('bucketName', bucketName);
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch('/api/ai-assistant/upload-files', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to upload files');
      }

      return result;
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  };

  const embedBucket = async (bucketName) => {
    try {
      const token = await user.getIdToken();
      
      const response = await fetch('/api/ai-assistant/embed-bucket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          bucketName: bucketName,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to embed bucket');
      }

      return result;
    } catch (error) {
      console.error('Error embedding bucket:', error);
      throw error;
    }
  };

  const updateAssistantKnowledge = async (assistantId, bucketIds) => {
    try {
      const token = await user.getIdToken();
      
      const response = await fetch('/api/ai-assistant/update-knowledge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          assistantId: assistantId,
          bucketIds: bucketIds,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to update knowledge folders');
      }

      return result;
    } catch (error) {
      console.error('Error updating assistant knowledge:', error);
      throw error;
    }
  };

  const openKnowledgeManager = async (assistant) => {
    setSelectedAssistantForKnowledge(assistant);
    
    // Load available buckets
    const buckets = await loadAvailableBuckets();
    setAvailableBuckets(buckets);
    
    // Extract current bucket IDs from assistant's tools
    const retrievalTool = assistant.telnyxData?.tools?.find(tool => tool.type === 'retrieval');
    const currentBuckets = retrievalTool?.retrieval?.bucket_ids || [];
    setAssistantKnowledgeFolders(currentBuckets);
    
    setShowKnowledgeDialog(true);
  };

  const deleteAssistant = async (assistantId, telnyxAssistantId) => {
    if (!window.confirm('Are you sure you want to delete this assistant? This action cannot be undone.')) {
      return;
    }

    try {
      // Get user's auth token
      const idToken = await user.getIdToken();

      // Call API to delete Telnyx assistant
      await fetch('/api/ai-assistant/delete', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          uid: user.uid,
          assistantId: telnyxAssistantId
        }),
      });

      // Delete from Firestore
      await deleteDoc(doc(db, 'aiAssistant', user.uid, 'assistants', assistantId));

      // Refresh assistants
      await loadAssistants(user.uid);
    } catch (error) {
      console.error('Error deleting assistant:', error);
      alert('Error deleting assistant: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-white" 
           style={{background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'}}>
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col min-h-screen text-white" 
           style={{background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'}}>
        
        {/* Mobile sidebar */}
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                  <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                  </button>
                </div>
              </TransitionChild>

              {/* Sidebar component */}
              <div 
                className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl"
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
                }}
              >
                <div className="flex h-16 shrink-0 items-center justify-center px-4">
                  <img alt="Relay" src="/images/logowhite.png" className="h-8 w-auto" />
                </div>
                <nav className="flex flex-1 flex-col px-4">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="space-y-3">
                        {navigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200"
                              onClick={() => setSidebarOpen(false)}
                            >
                              <item.icon 
                                aria-hidden="true" 
                                className={classNames(
                                  "h-6 w-6 shrink-0 transition-colors duration-200",
                                  item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                                )}
                              />
                              <span className={classNames(
                                "transition-colors duration-200",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )}>
                                {item.name}
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                    <li className="mt-auto mb-4">
                      <Link
                        to={settingsNavItem.href}
                        className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <settingsNavItem.icon 
                          aria-hidden="true" 
                          className={classNames(
                            "h-6 w-6 shrink-0 transition-colors duration-200",
                            settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                          )} 
                        />
                        <span className={classNames(
                          "transition-colors duration-200",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )}>
                          {settingsNavItem.name}
                        </span>
                      </Link>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Desktop sidebar */}
        <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ${
          sidebarExpanded ? 'lg:w-72' : 'lg:w-20'
        }`}>
          <div className="relative">
            <div 
              className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl relative"
              style={{
                background: 'rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)',
                height: 'calc(100vh - 2rem)'
              }}
            >
              {/* Header with logo */}
              <div className={`flex h-16 shrink-0 items-center justify-center transition-all duration-300 ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
                <img
                  alt="Relay"
                  src="/images/logowhite.png"
                  className={`transition-all duration-300 ${sidebarExpanded ? 'h-8 w-auto' : 'h-10 w-auto max-w-none'}`}
                  style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.1))' }}
                />
              </div>

              <nav className={`flex flex-1 flex-col ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="space-y-3">
                      {navigation.map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            className={classNames(
                              'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg',
                              sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                            )}
                            title={!sidebarExpanded ? item.name : undefined}
                          >
                            <item.icon 
                              aria-hidden="true" 
                              className={classNames(
                                "h-6 w-6 shrink-0 transition-colors duration-200",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )} 
                            />
                            {sidebarExpanded && (
                              <span className={classNames(
                                "transition-opacity duration-300",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )}>
                                {item.name}
                              </span>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                  <li className="mt-auto mb-4">
                    <Link
                      to={settingsNavItem.href}
                      className={classNames(
                        'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg',
                        sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                      )}
                      title={!sidebarExpanded ? settingsNavItem.name : undefined}
                    >
                      <settingsNavItem.icon 
                        aria-hidden="true" 
                        className={classNames(
                          "h-6 w-6 shrink-0 transition-colors duration-200",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )} 
                      />
                      {sidebarExpanded && (
                        <span className={classNames(
                          "transition-opacity duration-300",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )}>
                          {settingsNavItem.name}
                        </span>
                      )}
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>

            {/* Expand/collapse trigger */}
            <div 
              className="absolute top-0 right-0 w-4 h-full cursor-pointer group flex items-center justify-center"
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              style={{ zIndex: 60 }}
            >
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/10 backdrop-blur-md rounded-full p-1 border border-white/20">
                {sidebarExpanded ? (
                  <ChevronLeftIcon className="h-4 w-4 text-white/70" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-white/70" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className={`flex-1 transition-all duration-300 ${
          sidebarExpanded ? 'lg:pl-80' : 'lg:pl-28'
        }`}>
          {/* Header */}
          <div 
            className="flex h-16 shrink-0 items-center gap-x-4 border-b border-white/10 px-4 shadow-lg m-4 mt-0 mb-0 rounded-t-2xl backdrop-blur-2xl"
            style={{
              background: 'rgba(0, 0, 0, 0.2)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'
            }}
          >
            <button type="button" className="-m-2 p-3 text-white lg:hidden bg-white/5 backdrop-blur-md rounded-md hover:bg-white/10 transition-all duration-200" onClick={() => setSidebarOpen(true)}>
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
            <h1 className="text-lg sm:text-xl font-semibold text-white flex items-center gap-2">
              <SparklesIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              <span className="hidden sm:inline">AI Assistant Manager</span>
              <span className="sm:hidden">AI Assistants</span>
            </h1>
            <div className="ml-auto flex items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => setShowBucketDialog(true)}
                className="inline-flex items-center gap-x-1 sm:gap-x-2 rounded-xl bg-slate-600/60 backdrop-blur-sm px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-slate-200 hover:bg-slate-600/80 transition-all duration-200 border border-slate-500/30"
              >
                <FolderIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Knowledge Folder</span>
                <span className="sm:hidden">Folder</span>
              </button>
              <button
                type="button"
                onClick={() => setShowCreateDialog(true)}
                className="inline-flex items-center gap-x-1 sm:gap-x-2 rounded-xl bg-indigo-600/80 backdrop-blur-sm px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-white hover:bg-indigo-600 transition-all duration-200 border border-indigo-500/30"
              >
                <PlusIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Create Assistant</span>
                <span className="sm:hidden">Create</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 pt-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {/* Assistants */}
              {assistants.map((assistant) => (
                <div
                  key={assistant.id}
                  className="rounded-2xl border border-white/10 backdrop-blur-2xl p-6"
                  style={{
                    background: 'rgba(0, 0, 0, 0.2)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.2)'
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-indigo-600/20 border border-indigo-500/30">
                        <SparklesIcon className="h-6 w-6 text-indigo-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-white">{assistant.name}</h3>
                        <p className="text-sm text-white/60">Status: {assistant.status}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200"
                        title="Edit Assistant"
                      >
                        <PencilIcon className="h-4 w-4 text-white/60" />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteAssistant(assistant.id, assistant.telnyxAssistantId)}
                        className="p-2 rounded-lg bg-red-600/20 hover:bg-red-600/30 transition-all duration-200"
                        title="Delete Assistant"
                      >
                        <TrashIcon className="h-4 w-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm text-white/70">
                      <MicrophoneIcon className="h-4 w-4" />
                      Voice: {voiceOptions.find(v => v.value === assistant.voice)?.label || assistant.voice}
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-white/70">
                      <PhoneIcon className="h-4 w-4" />
                      {assistant.phoneNumbers?.length || 0} Phone Number(s)
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-white/70">
                      <ChatBubbleLeftRightIcon className="h-4 w-4" />
                      Greeting: {assistant.greeting?.substring(0, 50)}...
                    </div>
                    
                    {assistant.phoneNumbers && assistant.phoneNumbers.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <p className="text-xs text-white/50 mb-2">Linked Phone Numbers:</p>
                        <div className="flex flex-wrap gap-1">
                          {assistant.phoneNumbers.map((number, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-md bg-indigo-600/20 text-xs text-indigo-400 border border-indigo-500/30"
                            >
                              {number}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Knowledge Folders */}
                    {assistant.telnyxData?.tools?.find(tool => tool.type === 'retrieval') && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <p className="text-xs text-white/50 mb-2">Knowledge Folders:</p>
                        <div className="flex flex-wrap gap-1">
                          {assistant.telnyxData.tools
                            .find(tool => tool.type === 'retrieval')
                            ?.retrieval?.bucket_ids?.map((bucketId, index) => {
                              // Find the user bucket for this bucketId
                              const userBucket = userBuckets.find(b => b.telnyxBucketName === bucketId);
                              return (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded-md bg-slate-600/20 text-xs text-slate-400 border border-slate-500/30"
                                >
                                  <FolderIcon className="h-3 w-3 mr-1" />
                                  {userBucket?.userGivenName || bucketId}
                                </span>
                              );
                            })
                          }
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* No assistants message */}
              {assistants.length === 0 && (
                <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                  <SparklesIcon className="h-12 w-12 text-white/40 mb-4" />
                  <h3 className="text-lg font-medium text-white/80 mb-2">No AI Assistants</h3>
                  <p className="text-white/60 mb-4">Create your first AI assistant to handle phone calls automatically.</p>
                  <button
                    type="button"
                    onClick={() => setShowCreateDialog(true)}
                    className="inline-flex items-center gap-x-2 rounded-xl bg-indigo-600/80 backdrop-blur-sm px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600 transition-all duration-200 border border-indigo-500/30"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Create Your First Assistant
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Assistant Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" 
             style={{background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)'}}>
          <div 
            className="rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 border border-white/10 backdrop-blur-2xl"
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
            }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <SparklesIcon className="h-6 w-6" />
                Create AI Assistant
              </h3>
              <button
                onClick={() => setShowCreateDialog(false)}
                className="p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
              >
                <XMarkIcon className="h-5 w-5 text-white/60" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Assistant Name *
                </label>
                <p className="text-xs text-white/60 mb-3">
                  Give your AI assistant a name. This helps you identify it when you have multiple assistants.
                </p>
                <input
                  type="text"
                  value={assistantName}
                  onChange={(e) => setAssistantName(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-3 text-white placeholder-white/40 focus:border-indigo-500 focus:ring-indigo-500 focus:bg-white/10"
                  placeholder="e.g., Customer Support Bot, Main Reception"
                />
              </div>

              {/* Voice Selection */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Voice
                </label>
                <p className="text-xs text-white/60 mb-3">
                  Choose how your AI assistant will sound when speaking to callers. Click the play button to hear a preview.
                </p>
                <div className="space-y-3">
                  {voiceOptions.map((voice) => (
                    <div 
                      key={voice.value}
                      className={`p-3 rounded-xl border transition-all duration-200 cursor-pointer ${
                        selectedVoice === voice.value 
                          ? 'border-indigo-500 bg-indigo-600/20' 
                          : 'border-white/10 bg-white/5 hover:bg-white/10'
                      }`}
                      onClick={() => setSelectedVoice(voice.value)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            selectedVoice === voice.value 
                              ? 'border-indigo-500 bg-indigo-500' 
                              : 'border-white/40'
                          }`}>
                            {selectedVoice === voice.value && (
                              <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                            )}
                          </div>
                          <div>
                            <div className="text-white font-medium text-sm">{voice.label}</div>
                            <div className="text-white/60 text-xs">{voice.description}</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Demo functionality - for now just show an alert
                            alert(`Voice demo would play here: "${voice.label}"\n\nSample: "Hello! Thank you for calling. How can I help you today?"`);
                          }}
                          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200"
                          title="Preview voice"
                        >
                          <PlayIcon className="h-4 w-4 text-white/80" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Instructions */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Instructions
                </label>
                <p className="text-xs text-white/60 mb-3">
                  Tell your AI assistant how to behave and what information to share with callers. Choose a template below or write your own.
                </p>
                
                {/* Template Selection */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-white/70 mb-2">
                    Quick Start Templates
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {instructionTemplates.map((template) => (
                      <button
                        key={template.name}
                        type="button"
                        onClick={() => {
                          setAssistantInstructions(template.instructions);
                          setAssistantGreeting(template.greeting);
                        }}
                        className="p-3 text-left rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-200"
                      >
                        <div className="text-white font-medium text-sm">{template.name}</div>
                        <div className="text-white/60 text-xs mt-1">{template.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <textarea
                  value={assistantInstructions}
                  onChange={(e) => setAssistantInstructions(e.target.value)}
                  rows={6}
                  className="w-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-3 text-white placeholder-white/40 focus:border-indigo-500 focus:ring-indigo-500 focus:bg-white/10"
                  placeholder="Tell your assistant how to behave, what information to share, your business hours, services, etc."
                />
              </div>

              {/* Greeting */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Greeting Message
                </label>
                <p className="text-xs text-white/60 mb-3">
                  This is the first thing callers will hear when your AI assistant answers the phone.
                </p>
                <textarea
                  value={assistantGreeting}
                  onChange={(e) => setAssistantGreeting(e.target.value)}
                  rows={2}
                  className="w-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-3 text-white placeholder-white/40 focus:border-indigo-500 focus:ring-indigo-500 focus:bg-white/10"
                  placeholder="e.g., Hello! Thank you for calling ABC Company. How can I help you today?"
                />
              </div>

              {/* Phone Numbers */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Link Phone Numbers *
                </label>
                <p className="text-xs text-white/60 mb-3">
                  Choose which phone numbers this AI assistant should answer. When someone calls these numbers, your AI will handle the call.
                </p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {phoneNumbers.length === 0 ? (
                    <p className="text-white/60 text-sm">No active phone numbers available. Please activate a number in your Relay dashboard first.</p>
                  ) : (
                    phoneNumbers.map((phone) => (
                      <label key={phone.id} className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedPhoneNumbers.includes(phone.number)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPhoneNumbers([...selectedPhoneNumbers, phone.number]);
                            } else {
                              setSelectedPhoneNumbers(selectedPhoneNumbers.filter(num => num !== phone.number));
                            }
                          }}
                          className="rounded border-white/20 bg-white/10 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-white/80">{phone.number}</span>
                        <span className="text-white/40 text-xs">({phone.type})</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Knowledge Folders (Optional) */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Knowledge Folders (Optional)
                </label>
                <p className="text-xs text-white/60 mb-3">
                  Connect knowledge folders to help your AI answer questions about your business. This is optional - your AI will work great without any folders.
                </p>
                {userBuckets.length === 0 ? (
                  <div className="p-3 rounded-xl bg-blue-600/20 border border-blue-500/30">
                    <p className="text-blue-200/80 text-xs">
                      No knowledge folders yet. You can create one using the "Knowledge Folder" button above, or skip this for now.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {userBuckets.map((bucket) => (
                      <label key={bucket.bucketId} className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedKnowledgeFolders.includes(bucket.telnyxBucketName)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedKnowledgeFolders([...selectedKnowledgeFolders, bucket.telnyxBucketName]);
                            } else {
                              setSelectedKnowledgeFolders(selectedKnowledgeFolders.filter(name => name !== bucket.telnyxBucketName));
                            }
                          }}
                          className="rounded border-white/20 bg-white/10 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-white/80">{bucket.userGivenName}</span>
                        <span className="text-white/40 text-xs">({bucket.documentCount || 0} docs)</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {createError && (
                <div className="p-3 rounded-xl bg-red-500/20 border border-red-500/30">
                  <p className="text-red-400 text-sm">{createError}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowCreateDialog(false)}
                  className="rounded-xl bg-white/10 backdrop-blur-sm px-4 py-2 text-sm font-medium text-white hover:bg-white/20 transition-all duration-200 border border-white/10"
                >
                  Cancel
                </button>
                <button
                  onClick={createAssistant}
                  disabled={isCreating}
                  className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isCreating ? 'Creating...' : 'Create Assistant'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Bucket Dialog */}
      {showBucketDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" 
             style={{background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)'}}>
          <div 
            className="rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 border border-white/10 backdrop-blur-2xl"
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
            }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                <FolderIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                <span className="hidden sm:inline">Create Knowledge Folder</span>
                <span className="sm:hidden">Knowledge Folder</span>
              </h3>
              <button
                onClick={() => setShowBucketDialog(false)}
                className="p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
              >
                <XMarkIcon className="h-5 w-5 text-white/60" />
              </button>
            </div>

            <div className="mb-6 p-4 rounded-xl bg-blue-600/20 border border-blue-500/30">
              <div className="flex items-start gap-3">
                <FolderIcon className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-blue-400 font-medium text-sm mb-1">How it works</h4>
                  <p className="text-blue-200/80 text-xs leading-relaxed">
                    Add your website URL and choose a folder name. We'll crawl your website and store the content 
                    so your AI assistant can answer questions about your business. If the folder doesn't exist, 
                    it will be created automatically.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Mode Selection */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-3">
                  What would you like to add?
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setUploadMode('url')}
                    className={`flex-1 p-3 rounded-xl border transition-all duration-200 ${
                      uploadMode === 'url'
                        ? 'border-indigo-500 bg-indigo-600/20 text-white'
                        : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-sm font-medium">Website URL</div>
                      <div className="text-xs mt-1">Crawl website content</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploadMode('files')}
                    className={`flex-1 p-3 rounded-xl border transition-all duration-200 ${
                      uploadMode === 'files'
                        ? 'border-indigo-500 bg-indigo-600/20 text-white'
                        : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-sm font-medium">Upload Files</div>
                      <div className="text-xs mt-1">Documents, audio, video, etc.</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* URL Input */}
              {uploadMode === 'url' && (
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Website URL *
                  </label>
                  <p className="text-xs text-white/60 mb-3">
                    Enter your website URL. We'll crawl your site and extract the content so your AI can answer questions about your business.
                  </p>
                  <input
                    type="url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    className="w-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-3 text-white placeholder-white/40 focus:border-indigo-500 focus:ring-indigo-500 focus:bg-white/10"
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              )}

              {/* File Upload */}
              {uploadMode === 'files' && (
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Upload Files *
                  </label>
                  <p className="text-xs text-white/60 mb-3">
                    Upload documents and media files for your AI to learn from. Max 100MB per file.
                  </p>
                  
                  {/* Supported file types */}
                  <div className="mb-3 p-3 rounded-lg bg-blue-600/20 border border-blue-500/30">
                    <p className="text-xs text-blue-200/80 mb-2">Supported file types:</p>
                    <div className="text-xs text-blue-200/60 space-y-1">
                      <div>• <strong>Documents:</strong> PDF, TXT, JSON, CSV, HTML</div>
                      <div>• <strong>Audio:</strong> MP3, WAV, M4A, MPGA (max 100MB)</div>
                      <div>• <strong>Video:</strong> MP4, MPEG, WEBM (max 100MB)</div>
                    </div>
                  </div>

                  <div className="relative">
                    <input
                      id="file-upload"
                      type="file"
                      multiple
                      onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      accept=".pdf,.html,.txt,.json,.csv,.mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm"
                    />
                    <label 
                      htmlFor="file-upload" 
                      className="block w-full rounded-xl border-2 border-dashed border-white/20 bg-white/5 backdrop-blur-sm p-6 text-center hover:bg-white/10 transition-all duration-200 cursor-pointer"
                    >
                      <FolderIcon className="h-8 w-8 text-white/40 mx-auto mb-2 pointer-events-none" />
                      <p className="text-white/60 text-sm pointer-events-none">
                        {selectedFiles.length > 0 
                          ? `${selectedFiles.length} file(s) selected`
                          : 'Click to select files or drag & drop'
                        }
                      </p>
                      {selectedFiles.length > 0 && (
                        <div className="mt-3 space-y-1 max-h-32 overflow-y-auto pointer-events-none">
                          {selectedFiles.map((file, index) => (
                            <div key={index} className="text-xs text-white/50 truncate flex items-center justify-between">
                              <span>{file.name}</span>
                              <span className="text-white/40">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              )}

              <div>
                {userBuckets.length > 0 ? (
                  <>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      Knowledge Folder *
                    </label>
                    <p className="text-xs text-white/60 mb-3">
                      Choose an existing folder or create a new one.
                    </p>
                    <select
                      value={selectedBucket}
                      onChange={(e) => {
                        setSelectedBucket(e.target.value);
                        if (e.target.value === 'new') {
                          setBucketName('');
                        } else {
                          const selectedUserBucket = userBuckets.find(b => b.telnyxBucketName === e.target.value);
                          if (selectedUserBucket) {
                            setBucketName(selectedUserBucket.userGivenName);
                          }
                        }
                      }}
                      className="w-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-3 text-white focus:border-indigo-500 focus:ring-indigo-500 focus:bg-white/10"
                    >
                      <option value="">Select an existing folder</option>
                      {userBuckets.map((bucket) => (
                        <option key={bucket.bucketId} value={bucket.telnyxBucketName}>
                          {bucket.userGivenName}
                        </option>
                      ))}
                      <option value="new">+ Create new folder</option>
                    </select>
                    
                    {selectedBucket === 'new' && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-white/80 mb-2">
                          New Folder Name *
                        </label>
                        <input
                          type="text"
                          value={bucketName}
                          onChange={(e) => setBucketName(e.target.value)}
                          className="w-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-3 text-white placeholder-white/40 focus:border-indigo-500 focus:ring-indigo-500 focus:bg-white/10"
                          placeholder="e.g., company-website, product-info, support-docs"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      Knowledge Folder Name *
                    </label>
                    <p className="text-xs text-white/60 mb-3">
                      Enter a name for your knowledge folder. Use simple names like "website" or "support-docs".
                    </p>
                    <input
                      type="text"
                      value={bucketName}
                      onChange={(e) => setBucketName(e.target.value)}
                      className="w-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-3 text-white placeholder-white/40 focus:border-indigo-500 focus:ring-indigo-500 focus:bg-white/10"
                      placeholder="e.g., company-website, product-info, support-docs"
                    />
                  </>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowBucketDialog(false)}
                  className="rounded-xl bg-white/10 backdrop-blur-sm px-4 py-2 text-sm font-medium text-white hover:bg-white/20 transition-all duration-200 border border-white/10"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    // Validation based on mode
                    if (uploadMode === 'url') {
                      if (!websiteUrl.trim()) {
                        alert('Website URL is required');
                        return;
                      }
                    } else if (uploadMode === 'files') {
                      if (selectedFiles.length === 0) {
                        alert('Please select at least one file');
                        return;
                      }
                      
                      // Validate file sizes (max 100MB)
                      const maxSizeBytes = 100 * 1024 * 1024; // 100MB in bytes
                      
                      for (const file of selectedFiles) {
                        if (file.size > maxSizeBytes) {
                          alert(`File "${file.name}" is too large. Maximum file size is 100MB.`);
                          return;
                        }
                      }
                    }
                    
                    // Validation for folder selection/creation
                    if (userBuckets.length > 0) {
                      if (!selectedBucket) {
                        alert('Please select an existing folder or choose to create a new one');
                        return;
                      }
                      if (selectedBucket === 'new' && !bucketName.trim()) {
                        alert('Please enter a name for the new folder');
                        return;
                      }
                    } else {
                      if (!bucketName.trim()) {
                        alert('Please enter a knowledge folder name');
                        return;
                      }
                    }
                    
                    setIsUploading(true);
                    setUploadSuccess(false);
                    
                    try {
                      let actualBucketName;
                      
                      // Determine which bucket to use
                      if (selectedBucket && selectedBucket !== 'new') {
                        // User selected an existing bucket
                        actualBucketName = selectedBucket;
                      } else {
                        // User is creating a new bucket or no existing buckets available
                        // Check if user already has a bucket with this name
                        const existingBucket = userBuckets.find(b => b.userGivenName === bucketName.trim());
                        if (existingBucket) {
                          actualBucketName = existingBucket.telnyxBucketName;
                        } else {
                          // Assign new bucket to user
                          const assignResult = await assignBucket(bucketName.trim());
                          actualBucketName = assignResult.telnyxBucketName;
                          
                          // Refresh user buckets
                          const updatedBuckets = await loadUserBuckets();
                          setUserBuckets(updatedBuckets);
                        }
                      }
                      
                      if (uploadMode === 'url') {
                        // Embed URL
                        const result = await embedURL(websiteUrl, actualBucketName);
                        setUploadMessage(`Successfully started embedding your website! Your AI assistant will be able to answer questions about your website content once processing is complete.`);
                      } else if (uploadMode === 'files') {
                        // Upload files to Telnyx Storage then embed the bucket
                        const result = await uploadFiles(selectedFiles, actualBucketName);
                        if (result.success) {
                          // Now embed the entire bucket to process all uploaded files
                          const embedResult = await embedBucket(actualBucketName);
                          setUploadMessage(`Successfully uploaded ${result.uploads.length} file(s) and started processing! Your AI assistant will be able to answer questions about your uploaded files once processing is complete.`);
                        }
                      }
                      
                      setUploadSuccess(true);
                      
                      // Auto-close after 3 seconds and reset form
                      setTimeout(() => {
                        setWebsiteUrl('');
                        setSelectedFiles([]);
                        setBucketName('');
                        setSelectedBucket('');
                        setUploadMode('url');
                        setShowBucketDialog(false);
                        setUploadSuccess(false);
                        setUploadMessage('');
                        setIsUploading(false);
                      }, 3000);
                      
                    } catch (error) {
                      setIsUploading(false);
                      alert('Error processing content: ' + error.message);
                    }
                  }}
                  disabled={isUploading}
                  className="rounded-xl bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? 'Processing...' : (uploadMode === 'url' ? 'Add Website Content' : 'Upload Files')}
                </button>
              </div>
            </div>
            
            {/* Success Overlay */}
            {uploadSuccess && (
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <div className="bg-green-600/90 backdrop-blur-xl rounded-xl p-6 mx-4 text-center border border-green-500/30">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">Success!</h4>
                  <p className="text-green-100 text-sm max-w-md">
                    {uploadMessage}
                  </p>
                  <p className="text-green-200 text-xs mt-3">
                    This dialog will close automatically...
                  </p>
                </div>
              </div>
            )}
            
          </div>
        </div>
      )}
    </>
  );
}
